import { BUTTON_LABELS, TAG_LABELS } from '@/constants/labels';
import { FEATURE_FLAGS } from '@/constants/feature-flags';
import { IManagedUserRecentSummary, IManagedUserSession } from '@/types';
import { ManagerTableLinkButton as LinkButton } from '../ManagerTableLinkButton';
import { ManagerTableUnlockButton as UnlockButton } from '../ManagerTableUnlockButton';
import { PassFailTag } from '@/components/Tags';
import { useMemo } from 'react';

interface RenderProps {
  /** The most recent certification attempt for the user */
  mostRecentCertificate: IManagedUserSession | null;
  /** Callback function to handle unlocking a user's certification attempts */
  onUnlockButtonClick: (record: IManagedUserRecentSummary) => void;
  /** Callback function to handle viewing a user's certification scorecard */
  onViewScorecardClick: (userId: string, data: IManagedUserSession) => void;
  /** The record containing the user's certification information */
  record: IManagedUserRecentSummary;
}

/**
 * Certification status cell for the manager dashboard table. This component is
 * responsible for displaying the certification status for a user, along with any
 * relevant actions (view scorecard, unlock attempts).
 *
 * The logic for displaying the certification status is as follows:
 *  - If there is a certification attempt, we display either "Pass" or "Fail"
 *    based on the result of the most recent attempt, along with a view button
 *    to view the scorecard for that certification attempt.
 *  - If the user has failed 2 or more attempts, we also display a locked icon to indicate
 *    that they are locked out of further attempts until they receive coaching.
 */
export const ManagerTableCellCertificationStatus: React.FC<RenderProps> = ({
  mostRecentCertificate,
  onUnlockButtonClick,
  onViewScorecardClick,
  record,
}) => {
  /**
   * If there is a certification attempt, we display either "Pass" or "Fail"
   * based on the result of the most recent attempt, along with a view button
   * to view the scorecard for that certification attempt.
   */
  const attempts = useMemo(() => mostRecentCertificate?.attemptCount || 0, [mostRecentCertificate]);

  /**
   * If the user has failed 2 or more attempts, we also display a locked icon to indicate
   * that they are locked out of further attempts until they receive coaching.
   */
  const locked = useMemo(() => attempts >= 2, [attempts]);

  /**
   * Text to display for a failed attempt based on the number of attempts.
   *
   *  - If there is 1 failed attempt, we display "Failed - 1st attempt"
   *  - If there are 2 failed attempts, we display "Failed - 2nd attempt"
   *  - If there are 3 or more failed attempts, we display "Failed - 2+ attempts"
   *  - If there are no attempts, we display "Not attempted"
   */
  const failText = useMemo(() => {
    if (attempts === 1) {
      return TAG_LABELS.FAIL_1ST_ATTEMPT;
    }

    if (attempts === 2) {
      return TAG_LABELS.FAIL_2ND_ATTEMPT;
    }

    if (attempts > 2) {
      return TAG_LABELS.FAIL_2PLUS_ATTEMPTS;
    }

    return TAG_LABELS.NO_ATTEMPTS;
  }, [attempts]);

  // if there is no certification attempt, we display "Not attempted"
  if (mostRecentCertificate === null || mostRecentCertificate.attemptCount === 0) {
    return (
      <PassFailTag className="antialiased" simulationType="certification" variant="not attempted" />
    );
  }

  // ==============================================
  // Rendering logic
  // ==============================================
  return (
    <div className="flex items-center justify-start gap-2">
      <PassFailTag
        className="antialiased"
        simulationType="certification"
        textForFail={failText}
        variant={mostRecentCertificate.passed ? 'pass' : 'fail'}
      />

      {mostRecentCertificate.passed != null && (
        <LinkButton
          className="-mt-[1px]" // vertical alignment with the tag
          label={BUTTON_LABELS.VIEW}
          onClick={() => onViewScorecardClick(record.userId, mostRecentCertificate)}
        />
      )}

      {locked && FEATURE_FLAGS.ENABLE_CERTIFICATION_LOCKING && (
        <UnlockButton
          className="-mt-[1px]" // vertical alignment with the tag
          isLocked={true}
          onClick={() => onUnlockButtonClick(record)}
        />
      )}
    </div>
  );
};

ManagerTableCellCertificationStatus.displayName = 'ManagerTableCellCertificationStatus';

export default ManagerTableCellCertificationStatus;
